import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { getRecentDonorsByCampaign, getTopDonorsByCampaign } from "../../../api/services/donorService";
import { Images } from "../../../utils/Images";

const Donors = ({ campaignData }) => {
  const [tabValue, setTabValue] = useState(0);
  const [topDonors, setTopDonors] = useState([]);
  const [recentDonors, setRecentDonors] = useState([]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const campaignId = campaignData?.id;

  useEffect(() => {
    let isMounted = true;

    if (!campaignId) {
      setTopDonors([]);
      setRecentDonors([]);
      return undefined;
    }

    const fetchTopDonors = async () => {
      try {
        const donors = await getTopDonorsByCampaign(campaignId);
        if (isMounted) {
          setTopDonors(donors.slice(0, 8));
        }
      } catch (error) {
        console.error("Error fetching top donors:", error);
        if (isMounted) {
          setTopDonors([]);
        }
      }
    };

    const fetchRecentDonors = async () => {
      try {
        const donors = await getRecentDonorsByCampaign(campaignId, 8);
        if (isMounted && Array.isArray(donors)) {
          // Deduplicate by id in case API returns same donation twice
          const seen = new Set();
          const unique = donors.filter((d) => {
            const id = d?.id ?? `${d?.name}-${d?.amount}-${d?.donationDate}`;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
          });
          setRecentDonors(unique.slice(0, 8));
        }
      } catch (error) {
        console.error("Error fetching recent donors:", error);
        if (isMounted) {
          setRecentDonors([]);
        }
      }
    };

    fetchTopDonors();
    fetchRecentDonors();

    return () => {
      isMounted = false;
    };
  }, [campaignId]);

  const getAmountLabel = (amount, currency) => {
    if (!amount && amount !== 0) {
      return "";
    }

    const numericAmount = Number(amount);
    const formatted = Number.isFinite(numericAmount)
      ? numericAmount.toLocaleString(currency === "USD" ? "en-US" : "en-IN")
      : amount;

    return currency ? `${currency} ${formatted}` : formatted;
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  return (
    <Box sx={{ mx: "auto", p: 2, border: "1px solid #F1F1F1", boxShadow: "0 1.341px 10.727px 0 rgba(0, 0, 0, 0.10)", borderRadius: "12px", padding: "20px", width: "100%", alignSelf: "flex-start" }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: "26px" }}>
        <Typography variant="h4" component="h2" sx={{ display: "flex", gap: "10px", fontSize: isMobile ? "14px" : "24px", fontWeight: 'bold', color: '#1D1D1B' }}>
          <img src={Images.streamlineSharpStar} alt="Images.streamlineSharpStar" /> Donors
        </Typography>
        
        {/* Yellow Heart Icon and Tabs */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? "8px" : "42px" }}>
          {/* Yellow Heart Icon */}
          <Box
            component="img"
            src={Images.yellowHearts}
            alt="Yellow Heart"
            sx={{
              width: isMobile ? "20px" : "44px",
              height: isMobile ? "20px" : "44px",
              flexShrink: 0,
              transform: "scaleX(-1)", // Invert horizontally
              filter: "brightness(0.75) contrast(1.1)", // Darken the image
            }}
          />
          
          {/* Tabs */}
          <Box sx={{ border: "0.656px solid #EAEAF0", boxShadow: "0 6.561px 10.497px 0 rgba(102, 102, 102, 0.10)", borderRadius: "6.561px", padding: isMobile ? "5px" : "10px" }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="donors tabs"
            TabIndicatorProps={{
              style: {
                display: "none", 
              }
            }}
            sx={{
              minHeight: "unset",
              '& .MuiTabs-flexContainer': {
                position: "relative",
                zIndex: 1
              }
            }}
          >
            <Tab 
              label="Top Donors" 
              sx={{ 
                fontWeight: '600',
                padding: isMobile ? "10px 13px" : "12px 20px",
                borderRadius: "10px",
                minHeight: "unset",
                color: '#4A4A49',
                fontSize: isMobile ? "10px" : "14px",
                backgroundColor: tabValue === 0 ? '#9CBA4D' : '#fff',
                transition: "all 0.3s ease",
                '&.Mui-selected': {
                  color: 'white',
                }
              }} 
            />
            <Tab 
              label="Recent Donors" 
              sx={{ 
                fontWeight: '600',
                padding: isMobile ? "10px 13px" : "12px 20px",
                borderRadius: "10px",
                minHeight: "unset",
                color: '#4A4A49',
                fontSize: isMobile ? "10px" : "14px",
                backgroundColor: tabValue === 1 ? '#9CBA4D' : '#fff',
                transition: "all 0.3s ease",
                '&.Mui-selected': {
                  color: 'white',
                }
              }} 
            />
          </Tabs>
          </Box>
        </Box>
      </Box>
      
      {/* Donors List */}
      <Box>
        {tabValue === 0 ? (
          // Top Donors Tab
          topDonors.length > 0 ? (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: "repeat(auto-fit, minmax(223px, 1fr))", 
              gap: isMobile ? "7px" : "20px",
              rowGap: isMobile ? "15px" : "20px",
              alignItems: "stretch",
              alignContent: "start",
              justifyContent: "start",
              '@media (max-width: 448px)': {
                gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))"
              }
            }}>
              {topDonors.map((donor, index) => (
                <Box key={donor.id || `top-${index}`} sx={{ height: "100%" }}>
                  <Box sx={{ height: "100%", display: 'flex', alignItems: 'center', gap: "12px", p: isMobile ? "8px" : "14px", borderRadius: isMobile ? "8px" : '10px', backgroundColor: '#fff', border: "1px solid #EAEAF0", boxShadow: "0 0 13px 0 rgba(70, 79, 175, 0.10)" }}>
                    <Avatar 
                      src={donor.avatar || undefined}
                      sx={{ 
                        width: isMobile ? "23px" : 40, 
                        height: isMobile ? "23px" : 40,
                        fontWeight: '600'
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: '500', color: "#4A4A49", fontSize: isMobile ? "10px" : "14px", mb: isMobile ? "2px" : '4px' }}>
                        {donor.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#1D1D1B', fontSize: isMobile ? "8px" : "14px", fontWeight: "500" }}>
                        <strong> Donated -</strong> <span style={{ color: "#8EA946", fontWeight: "700" }}>{getAmountLabel(donor.amount, donor.currency)}</span> 
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: isMobile ? "40px 20px" : "60px 40px",
              textAlign: 'center'
            }}>
              <Typography sx={{ 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: 600, 
                color: "#4A4A49", 
                mb: "8px" 
              }}>
                No donors yet
              </Typography>
              <Typography sx={{ 
                fontSize: isMobile ? "14px" : "16px", 
                fontWeight: 400, 
                color: "#4A4A49" 
              }}>
                Be the first one to donate
              </Typography>
            </Box>
          )
        ) : (
          // Recent Donors Tab
          recentDonors.length > 0 ? (
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: "repeat(auto-fit, minmax(223px, 1fr))", 
              gap: isMobile ? "7px" : "20px",
              rowGap: isMobile ? "15px" : "20px",
              alignItems: "stretch",
              alignContent: "start",
              justifyContent: "start",
              '@media (max-width: 448px)': {
                gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))"
              }
            }}>
              {recentDonors.map((donor, index) => (
                <Box key={donor.id || `recent-${index}`} sx={{ height: "100%" }}>
                  <Box sx={{ height: "100%", display: 'flex', alignItems: 'center', gap: "12px", p: isMobile ? "8px" : "14px", borderRadius: isMobile ? "8px" : '10px', backgroundColor: '#fff', border: "1px solid #EAEAF0", boxShadow: "0 0 13px 0 rgba(70, 79, 175, 0.10)" }}>
                    <Avatar 
                      src={donor.avatar || undefined}
                      sx={{ 
                        width: isMobile ? "23px" : 40, 
                        height: isMobile ? "23px" : 40,
                        fontWeight: '600'
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: "500",
                          color: "#4A4A49",
                          fontSize: isMobile ? "10px" : "14px",
                          mb: isMobile ? "2px" : "4px",
                          // Allow wrapping so card height can grow (and grid will stretch siblings)
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        {donor.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#1D1D1B",
                          fontSize: isMobile ? "8px" : "14px",
                          fontWeight: "500",
                          // Keep donation line readable on smaller widths
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        <strong> Donated -</strong> <span style={{ color: "#8EA946", fontWeight: "700" }}>{getAmountLabel(donor.amount, donor.currency)}</span> 
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              padding: isMobile ? "40px 20px" : "60px 40px",
              textAlign: 'center'
            }}>
              <Typography sx={{ 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: 600, 
                color: "#4A4A49", 
                mb: "8px" 
              }}>
                No donors yet
              </Typography>
              <Typography sx={{ 
                fontSize: isMobile ? "14px" : "16px", 
                fontWeight: 400, 
                color: "#4A4A49" 
              }}>
                Be the first one to donate
              </Typography>
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default Donors;